import React, { useState, useEffect, useRef } from 'react';
import { documentTemplatesAPI } from '../../services/api';
import { TEMPLATE_VARIABLE_GROUPS } from '../../constants/templateVariables';
import '../EmailTemplates/emailTemplates.css';
import './documentTemplates.css';

// DocumentType enum values from the backend (Models/Document.cs). Photo is
// intentionally omitted — it makes no sense for a generated document template.
const DOCUMENT_TYPES = [
  { value: 1, label: 'Contract' },
  { value: 3, label: 'Estimate' },
  { value: 2, label: 'Permit' },
  { value: 4, label: 'Other' },
];

const typeLabel = (value) =>
  DOCUMENT_TYPES.find(t => t.value === value)?.label || 'Other';

const DocumentTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', documentType: 4, htmlContent: '' });
  const [error, setError] = useState('');
  const [previewId, setPreviewId] = useState(null);
  const htmlRef = useRef(null);

  const insertVariable = (varName) => {
    const token = `{{${varName}}}`;
    const el = htmlRef.current;
    const current = form.htmlContent || '';
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    setForm(prev => ({ ...prev, htmlContent: next }));
    // Restore focus + caret position after React rerenders the controlled input.
    setTimeout(() => {
      if (!el) return;
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await documentTemplatesAPI.getAll();
      setTemplates(res.data);
    } catch (err) {
      console.error('Error loading document templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', documentType: 4, htmlContent: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({
      name: template.name,
      description: template.description || '',
      documentType: template.documentType,
      htmlContent: template.htmlContent,
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.htmlContent) {
      setError('Name and HTML content are required.');
      return;
    }

    const payload = {
      name: form.name,
      description: form.description,
      documentType: Number(form.documentType),
      htmlContent: form.htmlContent,
    };

    try {
      if (editing) {
        await documentTemplatesAPI.update(editing.id, payload);
      } else {
        await documentTemplatesAPI.create(payload);
      }
      setShowModal(false);
      loadTemplates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await documentTemplatesAPI.delete(id);
      loadTemplates();
    } catch (err) {
      console.error('Error deleting document template:', err);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setForm(prev => ({ ...prev, htmlContent: event.target.result }));
    };
    reader.readAsText(file);
    // Reset so uploading the same file again re-triggers onChange.
    e.target.value = '';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="email-templates-page">
      <div className="et-header">
        <div>
          <h1>Document Templates</h1>
          <p className="et-subtitle">Manage HTML templates that generate merged PDF documents for projects</p>
        </div>
        <button className="btn-create-template" onClick={openCreate}>
          <i className="fas fa-plus"></i> New Template
        </button>
      </div>

      {loading ? (
        <div className="et-loading">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="et-empty">
          <i className="fas fa-file-pdf"></i>
          <h3>No Templates Yet</h3>
          <p>Create your first document template to get started.</p>
          <button className="btn-create-template" onClick={openCreate}>
            <i className="fas fa-plus"></i> Create Template
          </button>
        </div>
      ) : (
        <div className="et-grid">
          {templates.map(t => (
            <div key={t.id} className="et-card">
              <div className="et-card-header">
                <h3>{t.name}</h3>
                <div className="et-card-actions">
                  <button className="et-btn-icon" onClick={() => setPreviewId(previewId === t.id ? null : t.id)} title="Preview">
                    <i className="fas fa-eye"></i>
                  </button>
                  <button className="et-btn-icon" onClick={() => openEdit(t)} title="Edit">
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="et-btn-icon danger" onClick={() => handleDelete(t.id)} title="Delete">
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <div className="et-card-subject">
                <span className="dt-type-badge">{typeLabel(t.documentType)}</span>
                {t.description && <span className="dt-card-desc">{t.description}</span>}
              </div>
              <div className="et-card-meta">
                <span>By {t.createdBy}</span>
                <span>Updated {formatDate(t.updatedAt)}</span>
              </div>
              {previewId === t.id && (
                <div className="et-card-preview">
                  <div className="et-preview-label">Preview</div>
                  <div className="et-preview-content" dangerouslySetInnerHTML={{ __html: t.htmlContent }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        // Closing is the X and Cancel only — no click-to-close on the backdrop.
        <div className="modal-overlay">
          <div className="modal-content et-modal">
            <div className="modal-header">
              <h2>{editing ? 'Edit Template' : 'New Template'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="et-error">{error}</div>}

              <div className="form-group">
                <label>Template Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Change Order Letter"
                />
              </div>

              <div className="dt-form-row">
                <div className="form-group">
                  <label>Document Type</label>
                  <select
                    value={form.documentType}
                    onChange={e => setForm(prev => ({ ...prev, documentType: Number(e.target.value) }))}
                  >
                    {DOCUMENT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Description <span className="dt-optional">(optional)</span></label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Short note about when to use this"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>HTML Content</label>
                <div className="et-upload-row">
                  <label className="et-upload-btn">
                    <i className="fas fa-upload"></i> Upload HTML File
                    <input type="file" accept=".html,.htm" onChange={handleFileUpload} hidden />
                  </label>
                  <span className="et-or">or paste below</span>
                </div>
                <textarea
                  ref={htmlRef}
                  value={form.htmlContent}
                  onChange={e => setForm(prev => ({ ...prev, htmlContent: e.target.value }))}
                  placeholder="Paste HTML content here... Click a variable below to insert it at the cursor."
                  rows={12}
                />
              </div>

              <div className="et-variables-hint">
                <strong>Available variables</strong>
                <span className="et-vars-help"> — click to insert at cursor</span>
                {TEMPLATE_VARIABLE_GROUPS.map(group => (
                  <div key={group.category} className="et-vars-group">
                    <em>{group.category}:</em>
                    {group.vars.map(name => (
                      <button
                        key={name}
                        type="button"
                        className="et-var-chip"
                        onClick={() => insertVariable(name)}
                        title={`Insert {{${name}}}`}
                      >
                        {`{{${name}}}`}
                      </button>
                    ))}
                  </div>
                ))}
              </div>

              {form.htmlContent && (
                <div className="et-modal-preview">
                  <label>Preview</label>
                  <div className="et-preview-content" dangerouslySetInnerHTML={{ __html: form.htmlContent }} />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleSave}>
                {editing ? 'Update' : 'Create'} Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentTemplates;
